import { z } from 'zod';

import { SLUG_RE } from '../texto';
import { TIPOS_TOPICO } from '../mqtt/topicos';

/**
 * Esquemas de la topología: puntos de acceso, carriles, dispositivos y el
 * catálogo de tipos de visita.
 *
 * Regla que atraviesa todo este archivo: **el slug no se edita.** Viaja dentro
 * de los tópicos MQTT que están configurados a mano en cada aparato de campo
 * (docs/05). Renombrar "Acceso Principal" a "Entrada Norte" debe cambiar lo que
 * ve el guardia y nada más; si además cambiara el slug, el lector UHF de ese
 * acceso seguiría publicando en un tópico que ya no escucha nadie y la pluma
 * simplemente dejaría de abrir. Por eso los esquemas de actualización no
 * incluyen `slug`: la regla vive en el tipo, no en un comentario.
 */

const slugSchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(SLUG_RE, 'Solo minúsculas, dígitos y guiones; sin acentos ni espacios');

// ── Puntos de acceso ──────────────────────────────────────────

export const TIPOS_PUNTO_ACCESO = [
  'PRINCIPAL',
  'SECUNDARIO',
  'SERVICIO',
  'PEATONAL',
  'EMERGENCIA',
] as const;
export type TipoPuntoAcceso = (typeof TIPOS_PUNTO_ACCESO)[number];

export const crearPuntoAccesoSchema = z.object({
  nombre: z.string().trim().min(1, 'El punto de acceso necesita nombre').max(80),
  /** Si se omite, el servidor lo deriva del nombre con `aSlug`. */
  slug: slugSchema.optional(),
  tipo: z.enum(TIPOS_PUNTO_ACCESO).default('PRINCIPAL'),
  /** `false` = acceso desatendido: solo abre por tag o QR válido. */
  atendido: z.boolean().default(true),
  ubicacion: z.string().trim().max(120).nullish(),
});

/**
 * `activo` se puede cambiar en los dos sentidos, y esa es la gracia.
 *
 * Dar de baja estaba, reactivar no: una reja apagada por error se quedaba
 * apagada para siempre y había que entrar a la base de datos. Una puerta de un
 * solo sentido en una pantalla de configuración es una trampa — quien la cruza
 * no puede saber que no hay vuelta hasta que ya la cruzó.
 */
export const actualizarPuntoAccesoSchema = crearPuntoAccesoSchema
  .omit({ slug: true })
  .partial()
  .extend({ activo: z.boolean().optional() });

export type CrearPuntoAcceso = z.infer<typeof crearPuntoAccesoSchema>;
export type ActualizarPuntoAcceso = z.infer<typeof actualizarPuntoAccesoSchema>;

// ── Carriles ──────────────────────────────────────────────────

export const SENTIDOS_CARRIL = ['ENTRADA', 'SALIDA', 'BIDIRECCIONAL'] as const;
export type SentidoCarril = (typeof SENTIDOS_CARRIL)[number];

/**
 * Para quién es el carril.
 *
 * `VISITAS` admite visitas **y también** condóminos —quien vive aquí puede
 * formarse en la fila de visitas si le queda de paso—; `CONDOMINOS` es solo para
 * tag o facial, y una visita no entra por ahí. La asimetría es deliberada y
 * viene del primer día.
 *
 * Estaba en la base desde el principio y **no se podía capturar**: el esquema no
 * lo declaraba, así que todo carril nacía como de visitas sin que nadie lo
 * decidiera.
 */
export const USOS_CARRIL = ['VISITAS', 'CONDOMINOS'] as const;
export type UsoCarril = (typeof USOS_CARRIL)[number];

export const crearCarrilSchema = z.object({
  nombre: z.string().trim().min(1, 'El carril necesita nombre').max(80),
  slug: slugSchema.optional(),
  sentido: z.enum(SENTIDOS_CARRIL).default('ENTRADA'),
  uso: z.enum(USOS_CARRIL).default('VISITAS'),
  orden: z.number().int().min(0).default(0),
  /**
   * Cuánto dura el pulso al relé. Es por carril porque no todas las plumas
   * responden igual: una lenta con 300 ms no alcanza a subir.
   */
  pulsoMs: z.number().int().min(100).max(10_000).default(800),
});

// `timeoutPasoS` **no se captura**. Era el plazo tras el cual, sin señal del
// sensor de paso, se daría por hecho que la pluma subió y nadie cruzó. Pedía un
// número que ninguna línea del sistema lee, y que además solo tendría sentido en
// un carril sin sensor: en esta instalación todos lo llevan. Pedir un dato que
// no se usa no es neutral —quien captura supone que hace algo—.

export const actualizarCarrilSchema = crearCarrilSchema
  .omit({ slug: true })
  .partial()
  .extend({ activo: z.boolean().optional() });

export type CrearCarril = z.infer<typeof crearCarrilSchema>;
export type ActualizarCarril = z.infer<typeof actualizarCarrilSchema>;

// ── Dispositivos ──────────────────────────────────────────────

export const TIPOS_DISPOSITIVO = [
  'KIOSCO',
  'LECTOR_QR',
  'CAMARA_CREDENCIAL',
  'LECTOR_UHF',
  'CONTROLADOR_PLUMA',
  'DETECTOR_PASO',
  'TELEFONO_IP',
  'LECTOR_FACIAL',
  'CAMARA_LPR',
  'CAMARA_ROSTRO',
] as const;
export type TipoDispositivo = (typeof TIPOS_DISPOSITIVO)[number];

/**
 * Qué segmento `{tipo}` del tópico le corresponde a cada aparato. Es la
 * traducción entre el modelo de datos y el contrato de docs/05, y vive aquí
 * para que no se invente en cada lugar que arma un tópico.
 */
export const TIPO_TOPICO_POR_DISPOSITIVO: Record<
  TipoDispositivo,
  (typeof TIPOS_TOPICO)[number] | null
> = {
  KIOSCO: 'kiosco',
  LECTOR_QR: 'qr',
  CAMARA_CREDENCIAL: 'camara',
  LECTOR_UHF: 'uhf',
  CONTROLADOR_PLUMA: 'pluma',
  DETECTOR_PASO: 'pluma',
  /** El teléfono va por SIP contra Asterisk, no por el bus (docs/05 §7). */
  TELEFONO_IP: null,
  /**
   * La terminal facial **tampoco habla MQTT**: empuja sus eventos por HTTP al
   * API (ISAPI `httpHosts`) y se le enrola por HTTP también. Tuvo asignado el
   * segmento `facial` y el portal le enseñaba cinco tópicos que nada publicaba
   * ni escuchaba; quien configurara el aparato con eso no vería llegar un solo
   * evento, y buscaría el fallo en el equipo. Lo suyo es su URL.
   */
  LECTOR_FACIAL: null,
  CAMARA_LPR: 'lpr',
  /**
   * La cámara de rostro **tampoco habla MQTT**, por lo mismo que la terminal
   * facial: se le pide la foto por ISAPI cuando hace falta. Darle tópicos sería
   * enseñarle a quien la instale cinco direcciones donde nunca va a pasar nada.
   */
  CAMARA_ROSTRO: null,
};

/** Aparatos que sirven al punto de acceso completo y no a un carril concreto. */
export const DISPOSITIVOS_SIN_CARRIL: readonly TipoDispositivo[] = ['KIOSCO', 'TELEFONO_IP'];

export const crearDispositivoSchema = z
  .object({
    nombre: z.string().trim().min(1, 'El dispositivo necesita nombre').max(80),
    tipo: z.enum(TIPOS_DISPOSITIVO),
    /**
     * Con el que el aparato se conecta al broker, y el segmento `{clienteId}`
     * de su tópico. Se exige forma de slug: un identificador con `+` o `#`
     * dejaría al aparato publicar fuera de su rama del árbol.
     */
    clienteId: slugSchema,
    carrilId: z.string().cuid().nullish(),
    modelo: z.string().trim().max(60).nullish(),
    ip: z.string().trim().ip({ message: 'IP inválida' }).nullish(),
    mac: z
      .string()
      .trim()
      .regex(/^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/, 'MAC inválida')
      .nullish(),
    firmware: z.string().trim().max(40).nullish(),
    ubicacion: z.string().trim().max(120).nullish(),

    /**
     * Con qué credenciales **Kodica llama al aparato**. Hoy solo las usa la
     * terminal facial (ISAPI): para enrolar caras y para cargarle a dónde
     * empujar sus eventos. Vivían en variables de entorno, que alcanzan para
     * una sola terminal; en un condominio con una por carril, las caras de
     * todas se empujaban a la misma.
     *
     * La contraseña se escribe y **no se lee**: el API no la devuelve nunca.
     */
    usuarioAparato: z.string().trim().max(60).nullish(),
    passwordAparato: z.string().max(120).nullish(),
  })
  // Una pluma sin carril no se puede abrir: el comando se publica en el tópico
  // del carril. Es un error de configuración que hay que atrapar al dar de
  // alta y no la noche que alguien intente entrar.
  .refine((d) => DISPOSITIVOS_SIN_CARRIL.includes(d.tipo) || !!d.carrilId, {
    message: 'Este tipo de aparato tiene que colgar de un carril',
    path: ['carrilId'],
  });

// Lo mismo que al dar de alta, salvo lo que no se puede cambiar: el `tipo`
// —cambiarlo dejaría los tópicos apuntando a otra rama del árbol— y el
// `clienteId`, que es el segmento del tópico y desconectaría el aparato.
//
// **Faltaban `mac` y las credenciales**, así que no había forma de capturarlas
// después del alta: zod descarta las claves que no declara, de modo que un
// PATCH con ellas se guardaba a medias sin decir nada.
export const actualizarDispositivoSchema = z.object({
  activo: z.boolean().optional(),
  nombre: z.string().trim().min(1).max(80).optional(),
  carrilId: z.string().cuid().nullish(),
  modelo: z.string().trim().max(60).nullish(),
  ip: z.string().trim().ip({ message: 'IP inválida' }).nullish(),
  mac: z
    .string()
    .trim()
    .regex(/^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/, 'MAC inválida')
    .nullish(),
  firmware: z.string().trim().max(40).nullish(),
  ubicacion: z.string().trim().max(120).nullish(),
  usuarioAparato: z.string().trim().max(60).nullish(),
  passwordAparato: z.string().max(120).nullish(),
});

export type CrearDispositivo = z.infer<typeof crearDispositivoSchema>;
export type ActualizarDispositivo = z.infer<typeof actualizarDispositivoSchema>;

// ── Tipos de visita ───────────────────────────────────────────

export const crearTipoVisitaSchema = z.object({
  nombre: z.string().trim().min(1, 'El tipo de visita necesita nombre').max(40),
  /** El guardia los distingue por color antes que por texto. */
  color: z
    .string()
    .trim()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Color en formato #RRGGBB')
    .default('#64748b'),
  requiereFotoRostro: z.boolean().default(true),
  requiereFotoIdentificacion: z.boolean().default(true),
  requiereAutorizacion: z.boolean().default(true),
  orden: z.number().int().min(0).default(0),
});
export const actualizarTipoVisitaSchema = crearTipoVisitaSchema.partial();

export type CrearTipoVisita = z.infer<typeof crearTipoVisitaSchema>;
export type ActualizarTipoVisita = z.infer<typeof actualizarTipoVisitaSchema>;

/**
 * La contraseña con la que un aparato entra al API.
 *
 * Mínimo diez, como la de una persona: que nadie la teclee no la hace menos
 * capaz de abrir plumas — la cuenta del kiosco crea accesos y valida QR.
 */
export const passwordDeServicioSchema = z.object({
  password: z.string().min(10, 'Al menos 10 caracteres').max(72),
});
export type PasswordDeServicio = z.infer<typeof passwordDeServicioSchema>;
